import React, { useCallback, useEffect, useState, useRef } from 'react';
import { css } from '@emotion/react';
import { SwButtonSection, getFetcher } from '../../../App';
import { Input, PrimaryButton, Select, SelectSearchDicomTag } from '@components/atoms';
import he from 'he';
import { useSetRecoilState } from 'recoil';
import { dialogContentsState } from '@components/templates/Dialog';
import {
  StyledWarning,
  StyledcloseButton,
  StyledConfirmButton,
  StyledMetatag,
  StyledMetatagAddButton,
  StyledMetatagBox,
  StyledMetatagItem,
  StyledMetatagItemWrap,
  StyledTxtSuccess,
  StyledValue,
  StyleMetatagRow,
} from '@pages/SW/Metatag/style';
import _ from 'lodash';
import SWSettingCommonMethods from '../../../utils/settingCommon';
import { submitDebounce } from '../../../utils/lib';
export interface DicomInfo {
  definition: string;
  dicomTagId?: number;
  tag: string;
  value?: string;
  dataset?: string;
  valid?: boolean;
  isChanged?: boolean;
  action?: string;
  method?: string;
  vr?: string;
  isSequential?: boolean;
  isNumericVR?: boolean;
}

type selectList = {
  label: string;
  value: string;
  desc?: string;
  selected?: boolean;
};

const MetatagPage = () => {
  const [metaList, setMetaList] = useState<DicomInfo[]>([]);
  const [originMetaList, setOriginMetaList] = useState<DicomInfo[]>([]);
  const [isChanged, setIsChanged] = useState(false);
  const [dicomTagList, setDicomTagList] = useState<DicomInfo[]>([]);
  const actionList = [
    {
      label: 'Ignore',
      value: 'IGNORE',
    },
    {
      label: 'Record',
      value: 'RECORD',
    },
  ];
  const methodList = [
    {
      label: 'Equals',
      value: 'EQUALS',
    },
    {
      label: 'Contains',
      value: 'CONTAINS',
    },
    {
      label: 'NotNull',
      value: 'NOTNULL',
    },
    {
      label: 'In',
      value: 'IN',
    },
  ];
  const setDialogContents = useSetRecoilState(dialogContentsState);
  const {
    makeMetaList,
    delMetaList,
    metaListStatus,
    metaListLocalValid,
    metaListChangeData,
    getDicomTagAll,
    errorHandler,
    checkValidAllClear,
  } = SWSettingCommonMethods();
  const tagValidIndex = useRef(0);
  /**
   * dicom tag 가져오기
   */
  const getDicomTag = async () => {
    const _result = await getDicomTagAll();
    setDicomTagList(_result.result);
    await getMetaListSetting();
  };

  /**
   * metalist 설정 값 가져오기
   */
  const getMetaListSetting = async () => {
    const url = `api/setting/metatag`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    await getFetcher({
      method: 'GET',
      accessToken: token,
    })(url)
      .then((r) => {
        if (r.code === '200') {
          r.result.forEach((data: DicomInfo) => {
            data.isChanged = false;
            data.valid = true;
          });

          setMetaList(r.result);
          setOriginMetaList(r.result);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  const postTagValid = (index: number) => {
    submitDebounce(getCheckMetaTagValid);
  };

  /**
   * metaList 유효성 체크
   */
  const getCheckMetaTagValid = async (idx: number) => {
    if (!metaList[idx].tag || !metaList[idx].definition) return false;
    // 로컬 유효성 체크
    const _localValid = metaListLocalValid(idx, metaList);

    // 통과시 서버 유효성 체크
    if (_localValid) {
      // valid 체크
      const url = `api/setting/metatag/validate`;
      const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;
      const _params = {
        dicomTagId: metaList[idx].dicomTagId,
        dataset: metaList[idx].dataset,
        action: metaList[idx].action,
        method: metaList[idx].method,
      };

      await getFetcher({
        method: 'POST',
        accessToken: token,
        body: _params,
      })(url)
        .then(async (r) => {
          await metaListChange(r.code, idx, 'VALID');
        })
        .catch((e) => {
          console.log(e, 'e');
        });
    }
  };

  /**
   * 변경값 저장 하기
   */
  const postMetaListSetting = async () => {
    // valid 체크
    const url = `api/setting/metatag`;
    const token = localStorage.getItem('PROD_ADMIN_ACCESS_TOKEN') || undefined;

    const _tagList: any = [];

    metaList.forEach((data) => {
      const _tmp = {
        dicomTagId: data.dicomTagId,
        dataset: data.dataset,
        action: data.action,
        method: data.method,
      };
      _tagList.push(_tmp);
    });

    await getFetcher({
      method: 'POST',
      accessToken: token,
      body: _tagList,
    })(url)
      .then(async (r) => {
        if (r.code === '200') {
          setDialogContents({
            type: 'plain',
            body: `It has been reflected.`,
            footer: (
              <PrimaryButton
                onClick={async () => {
                  setDialogContents({ type: 'hidden' });
                }}
              >
                OK
              </PrimaryButton>
            ),
          });
          setOriginMetaList(metaList);
        } else {
          errorHandler(r);
        }
      })
      .catch((e) => {
        console.log(e, 'e');
      });
  };

  /**
   * Action selected
   * @param value
   * @param type
   * @param index
   */
  const selectAction = (value: any, type: string, index: number) => {
    metaListChange(value, index, type);
  };

  /**
   * Method selected
   * @param value
   * @param type
   * @param index
   */
  const selectMethod = (value: any, type: string, index: number) => {
    metaListChange(value, index, type);
  };

  /**
   * metaList 변경 또는 유효성 확인
   * @param e
   * @param index
   * @param type
   */
  const metaListChange = useCallback(
    async (e: any, index: number, type: string) => {
      await metaListChangeData(e, index, type, metaList, originMetaList, setMetaList);
    },
    [metaList, originMetaList],
  );

  /**
   * metalist 유효성 모두 통과 여부 확인
   */
  useEffect(() => {
    if (!metaList) {
      // send가 false 이면 기본 데이터 비교 해서 활성화 여부
      setIsChanged(metaList !== originMetaList);
    } else {
      // send가 true 이면 현재 변경 데이터 확인
      const _changeMetaList = checkValidAllClear(metaList, originMetaList);
      setIsChanged(_changeMetaList);
    }
  }, [metaList, originMetaList]);

  /**
   * report 사용여부, 변경 후 저장 안하고 페이지 나갈 경우, 이벤트
   * @param ev
   */
  const leave = (ev: any) => {
    ev.preventDefault();
    if (isChanged) {
      return (ev.returnValue = '사이트를 새로고침하시겠습니까? \n\r변경사항이 저장되지 않을 수 있습니다.');
    }
  };

  /**
   * 변경 안하고 나갈 경우(탭, 창을 닫음) 이벤트 감지
   */
  useEffect(() => {
    window.removeEventListener('beforeunload', leave);
    window.addEventListener('beforeunload', leave);
    return () => {
      window.removeEventListener('beforeunload', leave);
    };
  }, [isChanged]);

  /**
   * 최초 데이터 설정
   */
  useEffect(() => {
    (async () => {
      await getDicomTag();
    })();
  }, []);

  return (
    <>
      <label>
        Metatag <span className={'sub-title'}>Set the metatag for PACS communication.</span>
      </label>
      <StyledWarning>
        <span>
          If the metatag-value is not accurate, the analysis may proceed with an incorrect image, resulting in analysis
          errors and inappropriate results
        </span>
      </StyledWarning>
      <StyledMetatagItemWrap>
        <StyledMetatagBox>
          {/*
          StyledMetatagItem상태에 따라 클래스 추가/제거
          disable, active, error, success
        */}
          {metaList.length > 0 &&
            metaList.map((item, index) => {
              return (
                <StyledMetatagItem key={index} className={metaListStatus(item)}>
                  <StyleMetatagRow>
                    <StyledMetatag>
                      <div className="title">Metatag</div>
                      {/*검색*/}
                      <SelectSearchDicomTag
                        dicomTagList={dicomTagList}
                        value={item}
                        dataIdx={index}
                        onChangeFunc={metaListChange}
                      />
                    </StyledMetatag>
                    <StyledValue className="value">
                      <div className="title">Method</div>
                      <Select
                        key={metaList.length}
                        type={'METHOD'}
                        list={methodList}
                        selected={metaList[index].method}
                        onSelected={(value, type) => selectMethod(value, type, index)}
                      />
                    </StyledValue>
                  </StyleMetatagRow>

                  <StyleMetatagRow>
                    <StyledMetatag>
                      <div className="title">Action</div>
                      {/*검색*/}
                      <Select
                        key={metaList.length}
                        type={'ACTION'}
                        list={actionList}
                        selected={metaList[index].action}
                        onSelected={(value, type) => selectAction(value, type, index)}
                      />
                    </StyledMetatag>
                    <StyledValue className="value">
                      <div className="title">Value</div>
                      <Input
                        type={`text`}
                        name={`tel5`}
                        placeholder={`Value`}
                        value={item.dataset ? he.decode(item.dataset) : ''}
                        onChange={(e) => {
                          metaListChange(e, index, 'DATASET');
                        }}
                      >
                        {item.dataset && !item.valid && <p className="error-msg">Invalid value</p>}
                      </Input>
                    </StyledValue>
                  </StyleMetatagRow>

                  {item.tag && item.dataset && !item.isChanged && item.valid ? (
                    <StyledTxtSuccess>
                      <span>Success!</span>
                    </StyledTxtSuccess>
                  ) : (
                    <StyledConfirmButton
                      className="bnt-confirm"
                      onClick={submitDebounce(() => getCheckMetaTagValid(index))}
                    >
                      Confirm
                    </StyledConfirmButton>
                  )}
                  <StyledcloseButton onClick={() => delMetaList(index, metaList, setMetaList)} />
                </StyledMetatagItem>
              );
            })}
        </StyledMetatagBox>
        <StyledMetatagAddButton onClick={() => makeMetaList(metaList, setMetaList)}>
          <span>Add metatag</span>
        </StyledMetatagAddButton>
      </StyledMetatagItemWrap>

      <SwButtonSection>
        <PrimaryButton
          styles={css`
            width: 145px;
            height: 48px;
            margin-top: 8px;
            font-size: 18px;
          `}
          onClick={submitDebounce(postMetaListSetting)}
          disabled={!isChanged}
        >
          Submit
        </PrimaryButton>
      </SwButtonSection>
    </>
  );
};

export default MetatagPage;
